import { useDeferredValue, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Box, Button, HStack, IconButton, SimpleGrid, Stack, Tabs } from "@chakra-ui/react"
import { Text } from "@/components"
import { CompanyEmployeeCount, CompanyInfo, EsoDeal } from "@/types"
import { useCompanyEmployees, useDepartedEmployees } from "@/hooks"
import {
  AttritionChart,
  CapTableView,
  CompanyDeals,
  CompanyFiles,
  CompanyFinancialsView,
  CompanyFmvs,
  CompanyInfoCards,
  CompanyRumors,
  DealTabContent,
  DepartedEmployeesView,
  HeadCountChart,
  HiresAndDeparturesChart,
  PmfCrossoverChart,
} from "."

interface Props {
  companyInfo: CompanyInfo
  employeeCounts: CompanyEmployeeCount[]
  deal?: EsoDeal
  onRefreshGrants?: () => void
  isRefreshingGrants?: boolean
  dealPageMode?: boolean
  onSyncClick?: () => void
  isSyncing?: boolean
  onCalcToggle?: () => void
  isCalcOpen?: boolean
}

export const CompanyTabs = ({ companyInfo, employeeCounts, deal: fixedDeal, onRefreshGrants, isRefreshingGrants, dealPageMode, onSyncClick, isSyncing, onCalcToggle, isCalcOpen }: Props) => {
  const navigate = useNavigate()
  const [currentDeal, setCurrentDeal] = useState<EsoDeal | null>(null)
  const [comparisonDeal, setComparisonDeal] = useState<EsoDeal | null>(null)
  const [activeTab, setActiveTab] = useState(fixedDeal ? "deal" : "company-info")
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set())

  // Only fetch employee metrics when charts or departed tab are visible
  const needsEmployeeData = activeTab === "company-info" || activeTab === "departed-employees"
  const { data: employeeData } = useCompanyEmployees(companyInfo.id, needsEmployeeData)
  const deferredEmployeeData = useDeferredValue(employeeData)
  const { generalAttrition, salesAttrition, salesCount, engineeringCount, monthlyHiresAndDepartures } = deferredEmployeeData

  // Lazy-load full employee list only when departed employees tab is active
  const isDepartedTabActive = activeTab === "departed-employees"
  const { data: departedEmployees, isLoading: isDepartedLoading } = useDepartedEmployees(companyInfo.id, isDepartedTabActive)

  const activeDeal = fixedDeal ?? currentDeal

  const handleTabChange = (value: string) => {
    setActiveTab(value)
  }

  const handleDealClick = (deal: EsoDeal) => {
    if (fixedDeal) {
      setComparisonDeal(deal)
      setActiveTab("deal")
    } else {
      navigate(`/core/deal/${deal.id}`)
    }
  }

  const handleClearDeal = () => {
    if (fixedDeal) {
      setComparisonDeal(null)
    } else {
      setCurrentDeal(null)
      setActiveTab("deal-pipeline")
    }
  }

  return (
    <>
      {activeDeal && !fixedDeal && (
        <HStack
          w="fit-content"
          mt={3}
          p={2}
          px={3}
          bg="row.blue"
          borderRadius="md"
          border="1px solid"
          borderColor="blue.200"
          cursor="pointer"
          _hover={{ bg: "blue.100" }}
          onClick={() => setActiveTab("deal")}
        >
          <Text fontSize="sm" fontWeight="medium">
            Selected Deal: {activeDeal.client.firstName} {activeDeal.client.lastName}
          </Text>
          <IconButton
            aria-label="Clear selected deal"
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              handleClearDeal()
            }}
          >
            ✕
          </IconButton>
        </HStack>
      )}

      {fixedDeal && comparisonDeal && (
        <HStack
          w="fit-content"
          mt={3}
          p={2}
          px={3}
          bg="row.blue"
          borderRadius="md"
          border="1px solid"
          borderColor="blue.200"
        >
          <Text fontSize="sm" fontWeight="medium">
            Comparing with: {comparisonDeal.client.firstName} {comparisonDeal.client.lastName}
          </Text>
          <IconButton
            aria-label="Clear comparison deal"
            size="xs"
            variant="ghost"
            onClick={() => handleClearDeal()}
          >
            ✕
          </IconButton>
        </HStack>
      )}

      <Tabs.Root mt={5} value={activeTab} onValueChange={(details: { value: string }) => handleTabChange(details.value)} variant="line">
        <Tabs.List borderColor="border.default">
          {activeDeal && <Tabs.Trigger value="deal" color="blue">Deal</Tabs.Trigger>}
          <Tabs.Trigger value="company-info">Company Info</Tabs.Trigger>
          <Tabs.Trigger value="cap-table">Cap Table</Tabs.Trigger>
          <Tabs.Trigger value="fmvs">FMVs</Tabs.Trigger>
          <Tabs.Trigger value="rumors">Rumors</Tabs.Trigger>
          <Tabs.Trigger value="financials">Financials</Tabs.Trigger>
          <Tabs.Trigger value="departed-employees">Departed Employees</Tabs.Trigger>
          <Tabs.Trigger value="deal-pipeline">Past Offers</Tabs.Trigger>
          <Tabs.Trigger value="files">Files</Tabs.Trigger>
          <HStack ml="auto" gap={1}>
            <Button bg="#1C5180" color="white" size="xs" px={3} onClick={() => navigate(`/core/pitchbook/company/${companyInfo.id}`)}>
              PB
            </Button>
            {companyInfo.liStrId && (
              <Button bg="#0A66C2" color="white" size="xs" px={3} onClick={() => window.open(`https://www.linkedin.com/sales/company/${companyInfo.liStrId}`, "_blank")}>
                LI
              </Button>
            )}
          </HStack>
        </Tabs.List>

        <Tabs.Content value="company-info">
          <Stack my={5} gap={4}>
            <CompanyInfoCards
              fmvs={companyInfo.fmvs || []}
              capTableNote={companyInfo.capTableNotes}
              capTableFunding={companyInfo.capTableFunding || []}
            />
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
              <HeadCountChart
                employeeCounts={employeeCounts}
                salesCount={salesCount}
                engineeringCount={engineeringCount}
                capTableFunding={companyInfo.capTableFunding || []}
                foundingDate={companyInfo.capTableNotes?.foundingDate}
              />
              <AttritionChart
                generalAttrition={generalAttrition}
                salesAttrition={salesAttrition}
                employeeCounts={employeeCounts}
                capTableFunding={companyInfo.capTableFunding || []}
                foundingDate={companyInfo.capTableNotes?.foundingDate}
              />
              <HiresAndDeparturesChart
                data={monthlyHiresAndDepartures}
                capTableFunding={companyInfo.capTableFunding || []}
                foundingDate={companyInfo.capTableNotes?.foundingDate}
              />
              <PmfCrossoverChart
                salesCount={salesCount}
                engineeringCount={engineeringCount}
                employeeCounts={employeeCounts}
                capTableFunding={companyInfo.capTableFunding || []}
                foundingDate={companyInfo.capTableNotes?.foundingDate}
              />
            </SimpleGrid>
          </Stack>
        </Tabs.Content>

        <Tabs.Content value="cap-table">
          <Stack my={5}>
            <CapTableView
              companyId={companyInfo.id}
              capTableNote={companyInfo.capTableNotes}
              capTableFunding={companyInfo.capTableFunding || []}
              capTableComparisons={companyInfo.capTableComparisons}
              capTableMitigations={companyInfo.capTableMitigations}
            />
          </Stack>
        </Tabs.Content>

        <Tabs.Content value="deal-pipeline">
          <Stack my={5}>
            <CompanyDeals
              deals={companyInfo.deals || []}
              onDealClick={handleDealClick}
              selectedDealId={fixedDeal ? comparisonDeal?.id : undefined}
              onClearSelectedDeal={fixedDeal ? handleClearDeal : undefined}
            />
          </Stack>
        </Tabs.Content>

        <Tabs.Content value="fmvs">
          <Stack my={5}>
            <CompanyFmvs companyId={companyInfo.id} fmvs={companyInfo.fmvs || []} />
          </Stack>
        </Tabs.Content>

        <Tabs.Content value="rumors">
          <Stack my={5}>
            <CompanyRumors companyId={companyInfo.id} rumors={companyInfo.rumors || []} />
          </Stack>
        </Tabs.Content>

        <Tabs.Content value="financials">
          <Stack my={5}>
            <CompanyFinancialsView
              companyFinancials={companyInfo.companyFinancials?.[0] ?? null}
              capTableNote={companyInfo.capTableNotes}
            />
          </Stack>
        </Tabs.Content>

        <Tabs.Content value="departed-employees">
          <Stack my={5}>
            <DepartedEmployeesView
              isLoading={isDepartedLoading}
              employees={departedEmployees}
              selectedEmployees={selectedEmployees}
              onSelectionChange={setSelectedEmployees}
            />
          </Stack>
        </Tabs.Content>

        <Tabs.Content value="files">
          <Stack my={5}>
            <CompanyFiles companyId={companyInfo.id} />
          </Stack>
        </Tabs.Content>

        {activeDeal && (
          <Tabs.Content value="deal">
            <Stack my={5}>
              <DealTabContent deal={activeDeal} company={companyInfo} onRefreshGrants={onRefreshGrants} isRefreshingGrants={isRefreshingGrants} offerGeneratorOnly={dealPageMode} onSyncClick={onSyncClick} isSyncing={isSyncing} onCalcToggle={onCalcToggle} isCalcOpen={isCalcOpen} />
            </Stack>
          </Tabs.Content>
        )}
      </Tabs.Root>
    </>
  )
}
